<main class="text-start">
  <h1 class="page-title">{{ 'dashboard.delivery.title' | translate }}</h1>

  <div *ngIf="loaded && stats; else skeleton" class="bg-[#F6F6F6] rounded-md p-3.5 space-y-4">

    <!-- Stat boxes -->
    <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      <div class="bg-white rounded-xl px-3 py-4">
        <app-overview-card [title]="'dashboard.delivery.cards.unassigned' | translate" [number]="stats.unassigned"
          iconClass="bi bi-hourglass-split" iconColor="#FEC53D" iconBgColor="#FEF2D6"></app-overview-card>
      </div>
      <div class="bg-white rounded-xl px-3 py-4">
        <app-overview-card [title]="'dashboard.delivery.cards.out_for_delivery' | translate" [number]="stats.out_for_delivery"
          iconClass="bi bi-truck" iconColor="#80A4FF" iconBgColor="#E4ECFF"></app-overview-card>
      </div>
      <div class="bg-white rounded-xl px-3 py-4">
        <app-overview-card [title]="'dashboard.delivery.cards.delivered_today' | translate" [number]="stats.delivered_today"
          iconClass="bi bi-check2-circle" iconColor="#80FF8F" iconBgColor="#E4FFE7"></app-overview-card>
      </div>
      <div class="bg-white rounded-xl px-3 py-4">
        <app-overview-card [title]="'dashboard.delivery.cards.active_drivers' | translate" [number]="stats.active_drivers"
          [subDescription]="stats.total_drivers + ' ' + ('dashboard.delivery.cards.total_drivers' | translate)"
          iconClass="bi bi-person-badge" iconColor="#8280FF" iconBgColor="#E4E4FF"></app-overview-card>
      </div>
    </div>

    <!-- Deliveries over time -->
    <div class="w-full bg-white rounded-xl px-5 py-5">
      <div class="w-full flex justify-between items-center">
        <p class="section-title">{{ 'dashboard.delivery.charts.deliveries_over_time' | translate }}</p>
        <div class="w-[170px]">
          <app-date-range-picker-input
            [ngModel]="seriesRange"
            (ngModelChange)="onSeriesRangeChange($event)"
            [placeholder]="'dashboard.date_picker_last_month' | translate"
            [presets]="['thisWeek', 'thisMonth', 'lastMonth', 'last3Months', 'thisYear']"
            [lang]="languageService.currentLang()"
          />
        </div>
      </div>
      <div class="mt-6">
        <app-line-chart [labels]="deliveriesLabels" [datasets]="deliveriesData"></app-line-chart>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <!-- Delivery performance -->
      <div class="w-full bg-white rounded-xl px-5 py-5">
        <p class="section-title">{{ 'dashboard.delivery.performance.title' | translate }}</p>
        <div *ngIf="performance" class="mt-5">
          <p class="text-xs text-[#8E8E8E]">{{ 'dashboard.delivery.performance.avg_delivery_time' | translate }}</p>
          <div class="flex items-center gap-2 mt-1.5">
            <p class="text-[2rem] leading-none font-bold text-[#2F3541]">{{ formatDuration(performance.avg_delivery_minutes) }}</p>
            <span *ngIf="performance.trend_pct != null"
              class="text-[0.7rem] font-semibold inline-flex items-center px-1.5 py-0.5 rounded-full"
              [ngClass]="performance.trend_pct <= 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'">
              <i class="bi" [ngClass]="performance.trend_pct <= 0 ? 'bi-arrow-down-short' : 'bi-arrow-up-short'"></i>{{ abs(performance.trend_pct) }}%
            </span>
          </div>
          <p *ngIf="performance.trend_pct != null" class="text-[0.65rem] text-[#A1A1AA] mt-1.5">{{ 'dashboard.delivery.performance.vs_last_week' | translate }}</p>

          <div class="h-px bg-[#F0F0F0] my-5"></div>

          <div class="grid grid-cols-3 text-center divide-x divide-[#F0F0F0]">
            <div class="px-1">
              <p class="text-base font-semibold text-[#2F3541]">{{ performance.avg_deliveries_per_driver ?? '—' }}</p>
              <p class="text-[0.62rem] text-[#8E8E8E] mt-1 leading-tight">{{ 'dashboard.delivery.performance.avg_per_driver' | translate }}</p>
            </div>
            <div class="px-1">
              <p class="text-base font-semibold text-[#2F3541]">{{ performance.total_delivered }}</p>
              <p class="text-[0.62rem] text-[#8E8E8E] mt-1 leading-tight">{{ 'dashboard.delivery.performance.total_delivered' | translate }}</p>
            </div>
            <div class="px-1">
              <p class="text-base font-semibold text-[#2F3541]">{{ performance.fastest_weekday ? weekdayName(performance.fastest_weekday.weekday) : '—' }}</p>
              <p class="text-[0.62rem] text-[#8E8E8E] mt-1 leading-tight">{{ 'dashboard.delivery.performance.fastest_day' | translate }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Top drivers -->
      <div class="w-full bg-white rounded-xl px-5 py-5">
        <p class="section-title">{{ 'dashboard.delivery.charts.top_drivers' | translate }}</p>
        <div class="mt-4 flex flex-col gap-3">
          <a *ngFor="let driver of topDrivers; let i = index" [routerLink]="['/drivers', driver.id]"
            class="flex items-center gap-3 p-2 rounded-lg hover:bg-[#F6F6F6] transition-colors">
            <span class="text-[#8E8E8E] text-xs w-4">{{ i + 1 }}</span>
            <div class="bg-[#EDE7F6] w-9 h-9 rounded-full overflow-hidden flex items-center justify-center shrink-0">
              <img *ngIf="driver.profile_image; else dIcon" [src]="driver.profile_image" class="w-9 h-9 object-cover" alt="">
              <ng-template #dIcon><i class="bi bi-person-badge text-[#611F79]"></i></ng-template>
            </div>
            <div class="flex-1">
              <p class="text-xs font-medium text-[#2F3541]">{{ driver.name }}</p>
              <p class="text-[0.66rem] text-[#8E8E8E]">{{ driver.active_count }} {{ 'dashboard.delivery.active_now' | translate }}</p>
            </div>
            <div class="text-end">
              <p class="text-sm font-semibold text-[#2DCA72]">{{ driver.delivered_count }}</p>
              <p class="text-[0.6rem] text-[#8E8E8E]">{{ 'dashboard.delivery.delivered' | translate }}</p>
            </div>
          </a>
          <p *ngIf="topDrivers.length === 0" class="text-xs text-[#8E8E8E] text-center py-4">{{ 'dashboard.delivery.no_drivers' | translate }}</p>
        </div>
      </div>
    </div>

    <!-- Orders awaiting assignment -->
    <div class="w-full bg-white rounded-xl px-5 py-5">
      <div class="w-full flex justify-between items-center">
        <p class="section-title">{{ 'dashboard.delivery.awaiting_assignment' | translate }}</p>
        <a [routerLink]="['/orders']" class="text-xs text-[#0768F7]">{{ 'dashboard.more' | translate }}</a>
      </div>
      <div class="mt-4 overflow-x-auto">
        <table class="generic-table">
          <thead>
            <tr>
              <th>{{ 'orders.table.uuid' | translate }}</th>
              <th>{{ 'orders.table.status' | translate }}</th>
              <th class="md:table-cell hidden">{{ 'orders.details.recipient_details' | translate }}</th>
              <th class="md:table-cell hidden">{{ 'orders.table.placed_at' | translate }}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let order of unassignedResponse?.results" [routerLink]="['/orders', order.order_uuid]" class="cursor-pointer">
              <td class="p-3 text-gray-600">#{{ order.order_uuid | slice:0:8 }}</td>
              <td>
                <span class="inline-block py-1 px-2.5 rounded-full text-[0.7rem] font-medium text-nowrap"
                  [ngClass]="order.status === 'ready_for_pickup' ? 'bg-green-100 text-green-700' : order.status === 'preparing_order' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'">
                  {{ ('orders.statuses.' + order.status) | translate }}
                </span>
              </td>
              <td class="md:table-cell hidden">{{ order.recipient_name }}<span class="text-[#8E8E8E]"> · {{ order.recipient_city }}</span></td>
              <td class="md:table-cell hidden"><span dir="ltr">{{ order.placed_at | date: 'dd MMM / hh:mm a' }}</span></td>
              <td>
                <span class="inline-flex items-center gap-1 text-xs text-[#611F79]"><i class="bi bi-person-plus-fill"></i> {{ 'dashboard.delivery.assign' | translate }}</span>
              </td>
            </tr>
            <tr *ngIf="unassignedResponse && unassignedResponse.results.length === 0">
              <td colspan="100%" class="text-start text-xs text-[#8E8E8E]">{{ 'dashboard.delivery.no_unassigned' | translate }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <app-table-pagination *ngIf="unassignedResponse" [count]="unassignedResponse.count" [limit]="unassignedPagination.limit" [offset]="unassignedPagination.offset"
        (pageChange)="updateUnassignedPagination($event)" />
    </div>

  </div>

  <ng-template #skeleton>
    <div class="bg-[#F6F6F6] rounded-md p-3.5 space-y-4 animate-pulse">
      <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div *ngFor="let _ of [1,2,3,4]" class="bg-white rounded-xl h-20"></div>
      </div>
      <div class="bg-white rounded-xl h-64"></div>
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div class="bg-white rounded-xl h-64"></div>
        <div class="bg-white rounded-xl h-64"></div>
      </div>
      <div class="bg-white rounded-xl h-48"></div>
    </div>
  </ng-template>
</main>
